<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import { WidgetSeoContent } from '~/components/widget/seo'
import { WidgetRatesRelatedBanksRates } from '~/components/widget/rates'
import type { IBankByRelatedRates } from '~/components/widget/rates/related-banks-rates/types'

const props = withDefaults(defineProps<{
  banks?: IBankByRelatedRates[]
  hasMatchingRate?: boolean
}>(), {
  banks: undefined,
  hasMatchingRate: false
})

const route = useRoute()
const requestUrl = useRequestURL()
const baseUrl = requestUrl.origin
const currency = route.params.currency
const action: 'buy' | 'sell' = (route.name as string)?.includes('sell') ? 'sell' : 'buy'

const value = Number.isFinite(Number(route.params.value)) ? Number(route.params.value) : '0.00'

const currencyStore = useCurrencyStore()
await currencyStore.fetchCurrencies()
const { list } = storeToRefs(currencyStore)

const selectedCurrency = computed(() =>
  list.value.find((i) => i.code.toLowerCase() === (currency as string).toLowerCase())
)

const seoCurrency = computed(() => ({
  currency: {
    title: selectedCurrency.value?.title,
    description: selectedCurrency.value?.description,
    date: useDateFormat(new Date().toISOString()),
    value: value
  }
}))

const { t, locale } = useI18n()

const actionTitle = computed(() => {
  return action === 'buy' ? t('common.buy') : t('common.sell')
})

const actionUrl = computed(() => {
  return `/rates/${currency}/${action}`
})

const actionValueUrl = computed(() => {
  return `/rates/${currency}/${action}/${value}`
})

useSeoFromCustom(seoCurrency, baseUrl, locale.value)

const breadcrumbs = computed(() => [
  { title: t('common.exchange_rates'), url: '/rates' },
  { title: selectedCurrency.value?.title || '', url: `/rates/${currency}` },
  { title: actionTitle.value, url: actionUrl.value },
  { title: value.toString(), url: actionValueUrl.value }
])
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs
        :links="breadcrumbs"
        :variables="{ currency: selectedCurrency?.title || '', value: value.toString() }"
      />
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-4">
        <div class="space-y-5 xl:col-span-4">
          <WidgetSeoContent
            position="upper"
            :variables="{
              currency: selectedCurrency?.title || '',
              date: useDateFormat(new Date().toISOString()),
              value: value.toString()
            }"
          />
          <ContentBlock variant="white" class="text-lg">
            <WidgetRatesRelatedBanksRates
              v-if="props.banks && props.hasMatchingRate"
              :banks="props.banks"
              :currency="currency.toString()"
              :action="action"
              :rate="value"
            />
          </ContentBlock>
          <WidgetSeoContent
            position="bottom"
            :variables="{
              currency: selectedCurrency?.title || '',
              date: useDateFormat(new Date().toISOString()),
              value: value.toString()
            }"
          />
        </div>
      </div>
    </Container>
  </Main>
</template>
